import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CircleAlert, Info, X } from 'lucide-react';
import { BOOKING_STATUS_LABELS, type BookingStatus } from '@spacebooking/shared';
import { classNames } from '../lib/format';
import { useKey, useOnClickOutside } from '../lib/hooks';

// ---------- Field ----------

export function Field(props: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={classNames('field', props.className)}>
      <label className="field-label" htmlFor={id}>
        {props.label}
        {props.optional && <span className="optional">optional</span>}
      </label>
      {props.children(id)}
      {props.error ? <div className="field-error">{props.error}</div> : props.hint ? <div className="field-hint">{props.hint}</div> : null}
    </div>
  );
}

// ---------- Status ----------

export function StatusPill({ status }: { status: BookingStatus }) {
  return <span className={`badge status status-${status}`}>{BOOKING_STATUS_LABELS[status]}</span>;
}

export function Spinner({ className }: { className?: string }) {
  return <span className={classNames('spinner', className)} role="status" aria-label="Loading" />;
}

export function Empty(props: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {props.icon}
      <strong>{props.title}</strong>
      {props.children && <div style={{ maxWidth: 420 }}>{props.children}</div>}
      {props.action && <div style={{ marginTop: 8 }}>{props.action}</div>}
    </div>
  );
}

export function Notice(props: { tone?: 'info' | 'warning' | 'danger' | 'success'; children: ReactNode; className?: string }) {
  const tone = props.tone ?? 'info';
  const Icon = tone === 'danger' ? CircleAlert : tone === 'warning' ? AlertTriangle : tone === 'success' ? Check : Info;
  return (
    <div className={classNames('notice', `notice-${tone}`, props.className)} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon />
      <div>{props.children}</div>
    </div>
  );
}

// ---------- Overlays ----------

function Portal({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}

export function Dialog(props: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useKey('Escape', props.onClose, props.open);
  if (!props.open) return null;
  return (
    <Portal>
      <div className="overlay" onClick={props.onClose} />
      <div className={classNames('dialog', props.wide && 'dialog-wide')} role="dialog" aria-modal="true">
        <div className="dialog-head">
          <h2>{props.title}</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={props.onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="dialog-body">{props.children}</div>
        {props.footer && <div className="dialog-foot">{props.footer}</div>}
      </div>
    </Portal>
  );
}

export function Drawer(props: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useKey('Escape', props.onClose, props.open);
  if (!props.open) return null;
  return (
    <Portal>
      <div className="overlay" onClick={props.onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" style={props.width ? { width: `min(${props.width}px, 100vw)` } : undefined}>
        <div className="drawer-head">
          <div className="grow">{props.title}</div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={props.onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="drawer-body">{props.children}</div>
        {props.footer && <div className="drawer-foot">{props.footer}</div>}
      </aside>
    </Portal>
  );
}

/** Button that opens a small menu below it. */
export function MenuButton(props: {
  button: (open: boolean, toggle: () => void) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  up?: boolean;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false), open);
  useKey('Escape', () => setOpen(false), open);
  const close = () => setOpen(false);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {props.button(open, () => setOpen((o) => !o))}
      {open && (
        <div
          className="menu"
          role="menu"
          style={{
            [props.align === 'right' ? 'right' : 'left']: 0,
            [props.up ? 'bottom' : 'top']: 'calc(100% + 6px)',
            width: props.width,
          }}
        >
          {props.children(close)}
        </div>
      )}
    </div>
  );
}

export function ConfirmDialog(props: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title={props.title}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Keep it
          </button>
          <button className={classNames('btn', props.danger ? 'btn-danger' : 'btn-primary')} onClick={props.onConfirm} disabled={props.busy}>
            {props.busy && <Spinner />}
            {props.confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ color: 'var(--ink-2)' }}>{props.children}</div>
    </Dialog>
  );
}

// ---------- Toasts ----------

interface Toast {
  id: number;
  message: string;
  tone: 'default' | 'error';
}

const ToastContext = createContext<(message: string, tone?: 'default' | 'error') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const push = useCallback((message: string, tone: 'default' | 'error' = 'default') => {
    const id = ++counter.current;
    setToasts((t) => [...t.slice(-2), { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={classNames('toast', t.tone === 'error' && 'toast-error')}>
            {t.tone === 'error' ? <CircleAlert /> : <Check />}
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

/** Set document.title while mounted. */
export function useTitle(title: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}
